import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import {
  ArrowRight,
  CheckCircle,
  Clock,
  AlertTriangle,
  Zap,
  Target,
  Users,
  Sparkles
} from 'lucide-react';

type DemoStep = 'landing' | 'quality-spotting' | 'mission-alignment' | 'strategic-briefing' | 'conclusion';
type PainPoint = 'overwhelms' | 'generic' | 'timewaste';

interface Character {
  name: string;
  role: string;
  challenge: string;
  solution: string;
  iconPath: string;
  color: string;
}

const DemoOption2: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<DemoStep>('landing');
  const [selectedPainPoint, setSelectedPainPoint] = useState<PainPoint | null>(null);
  const [completedPaths, setCompletedPaths] = useState<PainPoint[]>([]);

  const characters: Record<PainPoint, Character> = {
    overwhelms: {
      name: "Maya Rodriguez",
      role: "Program Coordinator",
      challenge: "I feel overwhelmed by all the AI options and don't know what's actually useful",
      solution: "Quality Spotting System - Learn to identify and fix AI mistakes instantly",
      iconPath: "communication.png",
      color: "purple"
    },
    generic: {
      name: "Sofia Martinez",
      role: "Development Director",
      challenge: "AI sounds too corporate and generic for our mission-driven work",
      solution: "Mission Alignment System - Transform AI to match your authentic voice",
      iconPath: "mission-heart.png",
      color: "pink"
    },
    timewaste: {
      name: "Lyra",
      role: "AI Strategy Coach",
      challenge: "I waste too much time editing AI output instead of directing it properly",
      solution: "Strategic Briefing System - Direct AI like a professional from the start",
      iconPath: "lyra-avatar.png",
      color: "blue"
    }
  };

  const handlePainPointSelect = (painPoint: PainPoint) => {
    setSelectedPainPoint(painPoint);
    switch (painPoint) {
      case 'overwhelms':
        setCurrentStep('quality-spotting');
        break;
      case 'generic':
        setCurrentStep('mission-alignment');
        break;
      case 'timewaste':
        setCurrentStep('strategic-briefing');
        break;
    }
  };

  const completePath = (painPoint: PainPoint) => {
    if (!completedPaths.includes(painPoint)) {
      setCompletedPaths(prev => [...prev, painPoint]);
    }
  };

  if (currentStep === 'landing') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-6 py-16 max-w-6xl">
          {/* Logo/Brand Section */}
          <div className="text-center mb-16">
            <div className="w-32 h-32 mx-auto mb-8 bg-white rounded-3xl shadow-lg flex items-center justify-center">
              <SupabaseIcon
                iconPath="lyra-avatar.png"
                alt="Lyra AI Mentor"
                size="xl"
                className="w-20 h-20"
              />
            </div>

            <div className="mb-12">
              <div className="inline-block bg-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-lg">
                CHOOSE YOUR OWN AI ADVENTURE
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                What's Your Biggest AI Challenge?
              </h1>

              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-12">
                Every nonprofit professional faces different AI hurdles. Choose your path below and meet the character who conquered that exact challenge - then see their proven system in action.
              </p>
            </div>
          </div>

          {/* Pain Point Options */}
          <div className="space-y-8 mb-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Pick the challenge that frustrates you most:
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Option 1: AI Overwhelms Me */}
              <div
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => handlePainPointSelect('overwhelms')}
              >
                <div className="p-8">
                  <div className="w-20 h-20 rounded-2xl bg-purple-100 mx-auto mb-6 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <AlertTriangle className="w-10 h-10 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    "AI overwhelms me"
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-center">
                    Too many AI tools, conflicting advice, and you don't know what actually works for nonprofits. You need clarity, not more confusion.
                  </p>
                  <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl mb-6">
                    <div className="text-purple-800 font-semibold text-sm">You'll meet:</div>
                    <div className="text-purple-700 text-sm">Maya Rodriguez - Quality Spotting Expert</div>
                  </div>
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl shadow-md group-hover:shadow-lg transition-all">
                    <Target className="w-5 h-5 mr-2" />
                    Learn Quality Spotting
                  </Button>
                </div>
              </div>

              {/* Option 2: AI Sounds Generic */}
              <div
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => handlePainPointSelect('generic')}
              >
                <div className="p-8">
                  <div className="w-20 h-20 rounded-2xl bg-pink-100 mx-auto mb-6 flex items-center justify-center group-hover:bg-pink-200 transition-colors">
                    <Clock className="w-10 h-10 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    "AI sounds generic"
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-center">
                    AI gives you corporate-speak that doesn't match your mission. You need content that sounds authentically you, not like every other organization.
                  </p>
                  <div className="bg-pink-50 border border-pink-200 p-4 rounded-xl mb-6">
                    <div className="text-pink-800 font-semibold text-sm">You'll meet:</div>
                    <div className="text-pink-700 text-sm">Sofia Martinez - Mission Alignment Expert</div>
                  </div>
                  <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-xl shadow-md group-hover:shadow-lg transition-all">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Learn Mission Alignment
                  </Button>
                </div>
              </div>

              {/* Option 3: I Waste Time Editing AI */}
              <div
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => handlePainPointSelect('timewaste')}
              >
                <div className="p-8">
                  <div className="w-20 h-20 rounded-2xl bg-blue-100 mx-auto mb-6 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Zap className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    "I waste time editing AI"
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed text-center">
                    You spend more time fixing AI output than it would take to write from scratch. You need to learn how to direct AI properly from the start.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl mb-6">
                    <div className="text-blue-800 font-semibold text-sm">You'll meet:</div>
                    <div className="text-blue-700 text-sm">Lyra - Strategic Briefing Expert</div>
                  </div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl shadow-md group-hover:shadow-lg transition-all">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Learn Strategic Briefing
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="bg-white rounded-3xl shadow-lg p-8 mb-16 max-w-4xl mx-auto">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-purple-600 mb-4">Why Choose Your Path?</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Each path connects you with a real professional who faced your exact challenge. You'll see their proven system, experience their transformation, and get a clear roadmap for your own AI mastery.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                    <SupabaseIcon
                      iconPath="learning-target.png"
                      alt="Targeted Learning"
                      size="lg"
                      className="w-10 h-10"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-800">Targeted Solutions</h3>
                  <p className="text-sm text-gray-600">Address your specific pain point</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                    <Users className="w-8 h-8 text-gray-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Real Examples</h3>
                  <p className="text-sm text-gray-600">See actual transformations</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                    <SupabaseIcon
                      iconPath="achievement-trophy.png"
                      alt="Proven Methods"
                      size="lg"
                      className="w-10 h-10"
                    />
                  </div>
                  <h3 className="font-semibold text-gray-800">Proven Methods</h3>
                  <p className="text-sm text-gray-600">Battle-tested systems</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Character Introduction and Demo for each path
  const character = selectedPainPoint ? characters[selectedPainPoint] : null;
  if (!character) return null;

  if (currentStep === 'quality-spotting') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <Button
            variant="ghost"
            onClick={() => setCurrentStep('landing')}
            className="mb-6"
          >
            ← Choose Different Challenge
          </Button>

          {/* Character Introduction */}
          <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-6">
              <div className="w-20 h-20 rounded-2xl bg-purple-100 flex items-center justify-center">
                <SupabaseIcon
                  iconPath={character.iconPath}
                  alt={character.name}
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{character.name}</h2>
                <p className="text-purple-600 font-semibold mb-3">{character.role}</p>
                <p className="text-lg text-gray-700 mb-4 italic">"{character.challenge}"</p>
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl">
                  <div className="text-purple-800 font-semibold">Maya's Solution:</div>
                  <div className="text-purple-700">{character.solution}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quality Spotting Demo */}
          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Maya's Quality Spotting System</h3>
              <p className="text-gray-600 max-w-3xl mx-auto mb-8">
                "I used to trust AI blindly and send out emails with wrong facts. Now I spot problems instantly using my 3-step system."
              </p>
            </div>

            {/* Before/After Demo */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center">
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Before: AI Overwhelm
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border text-sm">
                      "Dear Supporter, Hope Gardens has served 300 families since 2015. Our Director thanks you for helping us reach our $50,000 goal."
                    </div>
                    <div className="text-xs text-red-600 space-y-1">
                      <div>❌ AI invented "300 families" (actually 120)</div>
                      <div>❌ Wrong founding year "2015" (actually 2018)</div>
                      <div>❌ Wrong title "Director" (Maya is Coordinator)</div>
                      <div>❌ Wrong goal "$50,000" (current campaign is $25,000)</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-green-700 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    After: Quality Control Mastery
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border text-sm">
                      "Dear Sarah, Hope Gardens has served 120 families since 2018. I'm Maya Rodriguez, Program Coordinator, and your $75 helps us reach our $25,000 goal to serve 15 more children."
                    </div>
                    <div className="text-xs text-green-600 space-y-1">
                      <div>✅ Correct family count verified</div>
                      <div>✅ Accurate founding year confirmed</div>
                      <div>✅ Proper title and name</div>
                      <div>✅ Current campaign goal verified</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Maya's 3-Step System */}
            <Card className="bg-purple-50 border-purple-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-purple-800">Maya's 3-Step Quality Control System</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-purple-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">1</div>
                    <h4 className="font-semibold mb-2">Fact Check</h4>
                    <p className="text-sm text-gray-600">Verify every number, date, and claim against your records</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-purple-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">2</div>
                    <h4 className="font-semibold mb-2">Voice Check</h4>
                    <p className="text-sm text-gray-600">Ensure tone matches your organization's authentic voice</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-purple-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">3</div>
                    <h4 className="font-semibold mb-2">Impact Check</h4>
                    <p className="text-sm text-gray-600">Confirm specific outcomes and beneficiary details</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Maya's Transformation Results</h4>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-green-600">0</div>
                  <div className="text-sm text-gray-600">Fact errors sent</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600">90%</div>
                  <div className="text-sm text-gray-600">Time saved on editing</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">100%</div>
                  <div className="text-sm text-gray-600">Confidence in AI output</div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button
                onClick={() => {
                  completePath('overwhelms');
                  setCurrentStep('conclusion');
                }}
                size="lg"
                className="bg-purple-600 hover:bg-purple-700"
              >
                Get Maya's Complete Quality System →
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'mission-alignment') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <Button
            variant="ghost"
            onClick={() => setCurrentStep('landing')}
            className="mb-6"
          >
            ← Choose Different Challenge
          </Button>

          {/* Character Introduction */}
          <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-6">
              <div className="w-20 h-20 rounded-2xl bg-pink-100 flex items-center justify-center">
                <SupabaseIcon
                  iconPath={character.iconPath}
                  alt={character.name}
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{character.name}</h2>
                <p className="text-pink-600 font-semibold mb-3">{character.role}</p>
                <p className="text-lg text-gray-700 mb-4 italic">"{character.challenge}"</p>
                <div className="bg-pink-50 border border-pink-200 p-4 rounded-xl">
                  <div className="text-pink-800 font-semibold">Sofia's Solution:</div>
                  <div className="text-pink-700">{character.solution}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission Alignment Demo */}
          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Sofia's Mission Alignment System</h3>
              <p className="text-gray-600 max-w-3xl mx-auto mb-8">
                "AI used to give me corporate fundraising templates that felt fake. Now every piece of content authentically represents our community-centered values."
              </p>
            </div>

            {/* Before/After Demo */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <Card className="border-gray-300 bg-gray-50">
                <CardHeader>
                  <CardTitle className="text-gray-700 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Before: Generic Corporate AI
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border text-sm">
                      "Dear Valued Stakeholder,<br/><br/>
                      We are pleased to announce our organization has achieved significant milestones this quarter. Your continued investment enables our strategic initiatives to drive sustainable impact.<br/><br/>
                      Thank you for your partnership."
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>❌ Corporate jargon ("stakeholder," "strategic initiatives")</div>
                      <div>❌ Vague claims ("significant milestones")</div>
                      <div>❌ Transactional tone</div>
                      <div>❌ No human connection</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-pink-200 bg-pink-50">
                <CardHeader>
                  <CardTitle className="text-pink-700 flex items-center">
                    <Sparkles className="w-5 h-5 mr-2" />
                    After: Mission-Aligned Voice
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border text-sm">
                      "Dear Maria,<br/><br/>
                      This month, 12 formerly homeless families moved into permanent housing - including the Rodriguez family you met at our volunteer dinner. Your $150 monthly gift directly funds the case management that makes these transformations possible.<br/><br/>
                      With deep gratitude, Sofia"
                    </div>
                    <div className="text-xs text-pink-600 space-y-1">
                      <div>✅ Personal connection ("Maria," "families you met")</div>
                      <div>✅ Specific impact ("12 families," "Rodriguez family")</div>
                      <div>✅ Warm, authentic tone</div>
                      <div>✅ Direct impact link ("$150 funds case management")</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sofia's Mission Alignment Framework */}
            <Card className="bg-pink-50 border-pink-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-pink-800">Sofia's Mission Alignment Framework</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-pink-600 text-white mx-auto mb-3 flex items-center justify-center">
                      <SupabaseIcon
                        iconPath="mission-heart.png"
                        alt="Heart"
                        size="sm"
                        className="w-6 h-6"
                      />
                    </div>
                    <h4 className="font-semibold mb-2">Values Check</h4>
                    <p className="text-xs text-gray-600">Every word reflects community-centered values</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-pink-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">👥</div>
                    <h4 className="font-semibold mb-2">Human Focus</h4>
                    <p className="text-xs text-gray-600">Stories center real people, not programs</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-pink-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">🤝</div>
                    <h4 className="font-semibold mb-2">Relationship Tone</h4>
                    <p className="text-xs text-gray-600">Partnership, not transaction language</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-pink-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">💫</div>
                    <h4 className="font-semibold mb-2">Hope Focus</h4>
                    <p className="text-xs text-gray-600">Emphasize transformation and possibility</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interactive Example */}
            <Card className="border-pink-200">
              <CardHeader>
                <CardTitle>Try Sofia's Voice Test</CardTitle>
                <CardDescription>Which version better reflects your nonprofit's authentic voice?</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-gray-100 p-3 rounded">
                    <strong>Scenario:</strong> Thanking a major donor for education program support
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button className="text-left p-3 border rounded hover:bg-gray-50">
                      <div className="text-sm text-gray-700">
                        "Your investment of $5,000 enables our educational programming to serve additional beneficiaries in our target demographic."
                      </div>
                      <div className="text-xs text-gray-500 mt-2">Corporate • Transactional • Vague</div>
                    </button>
                    <button className="text-left p-3 border rounded hover:bg-pink-50 border-pink-200">
                      <div className="text-sm text-pink-700">
                        "Your $5,000 gift means 20 more kids like Jamie can get after-school tutoring. Jamie jumped from C's to A's this semester - his mom says he now loves reading!"
                      </div>
                      <div className="text-xs text-pink-600 mt-2">Personal • Specific • Mission-aligned</div>
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Sofia's Mission Alignment Results</h4>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-pink-600">47%</div>
                  <div className="text-sm text-gray-600">Higher donor response rates</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-600">85%</div>
                  <div className="text-sm text-gray-600">Staff approve AI content instantly</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">100%</div>
                  <div className="text-sm text-gray-600">Authentic organizational voice</div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button
                onClick={() => {
                  completePath('generic');
                  setCurrentStep('conclusion');
                }}
                size="lg"
                className="bg-pink-600 hover:bg-pink-700"
              >
                Get Sofia's Complete Mission Alignment System →
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'strategic-briefing') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          <Button
            variant="ghost"
            onClick={() => setCurrentStep('landing')}
            className="mb-6"
          >
            ← Choose Different Challenge
          </Button>

          {/* Character Introduction */}
          <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-6">
              <div className="w-20 h-20 rounded-2xl bg-blue-100 flex items-center justify-center">
                <SupabaseIcon
                  iconPath={character.iconPath}
                  alt={character.name}
                  size="lg"
                  className="w-12 h-12"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{character.name}</h2>
                <p className="text-blue-600 font-semibold mb-3">{character.role}</p>
                <p className="text-lg text-gray-700 mb-4 italic">"{character.challenge}"</p>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
                  <div className="text-blue-800 font-semibold">Lyra's Solution:</div>
                  <div className="text-blue-700">{character.solution}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Strategic Briefing Demo */}
          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lyra's Strategic Briefing System</h3>
              <p className="text-gray-600 max-w-3xl mx-auto mb-8">
                "Most people ask AI like Google - vague questions that get mediocre answers. I'll show you how to brief AI like a professional director to get exactly what you need, first try."
              </p>
            </div>

            {/* Before/After Demo */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Before: Vague AI Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border text-sm font-mono">
                      <strong>Prompt:</strong> "Write a fundraising email"
                    </div>
                    <div className="bg-gray-100 p-3 rounded text-sm">
                      <strong>AI Output:</strong> Generic, corporate template requiring 45 minutes of editing
                    </div>
                    <div className="text-xs text-red-600 space-y-1">
                      <div>❌ No context or constraints</div>
                      <div>❌ Wrong tone and audience</div>
                      <div>❌ Missing specific details</div>
                      <div>❌ Requires extensive revision</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-blue-700 flex items-center">
                    <Target className="w-5 h-5 mr-2" />
                    After: Strategic AI Direction
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-white p-3 rounded border text-sm font-mono">
                      <strong>Strategic Brief:</strong> "You are Sofia Martinez, Director of Community Outreach & Project Manager at Casa de Esperanza. Write a 150-word email to major donor Maria Gonzalez thanking her for her $2,500 Q4 gift. Context: This gift helped 12 families secure permanent housing including the Rodriguez family Maria met at our volunteer dinner. Tone: Warm and personal but professional. Goal: Maintain relationship and set up Q1 cultivation lunch."
                    </div>
                    <div className="bg-blue-100 p-3 rounded text-sm">
                      <strong>AI Output:</strong> Perfect first draft ready to send in 30 seconds
                    </div>
                    <div className="text-xs text-blue-600 space-y-1">
                      <div>✅ Complete context and persona</div>
                      <div>✅ Specific details and constraints</div>
                      <div>✅ Clear goal and tone guidance</div>
                      <div>✅ Zero revision needed</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lyra's BRIEF Framework */}
            <Card className="bg-cyan-50 border-cyan-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-blue-800">Lyra's BRIEF Framework for AI Direction</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">B</div>
                    <h4 className="font-semibold mb-2">Background</h4>
                    <p className="text-xs text-gray-600">Who you are, what organization</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">R</div>
                    <h4 className="font-semibold mb-2">Role</h4>
                    <p className="text-xs text-gray-600">Specific voice and perspective</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">I</div>
                    <h4 className="font-semibold mb-2">Intent</h4>
                    <p className="text-xs text-gray-600">Clear goal and desired outcome</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">E</div>
                    <h4 className="font-semibold mb-2">Expectations</h4>
                    <p className="text-xs text-gray-600">Format, length, tone requirements</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white mx-auto mb-3 flex items-center justify-center font-bold">F</div>
                    <h4 className="font-semibold mb-2">Facts</h4>
                    <p className="text-xs text-gray-600">Specific details and constraints</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interactive Brief Builder */}
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle>Try Lyra's Brief Builder</CardTitle>
                <CardDescription>See how each element transforms AI output quality</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded">
                    <div className="text-sm font-semibold mb-2">Build Your Strategic Brief:</div>
                    <div className="space-y-2 text-sm">
                      <div><strong>Background:</strong> You are [Your Name], [Your Role] at [Organization]</div>
                      <div><strong>Role:</strong> Writing as the voice of [specific perspective]</div>
                      <div><strong>Intent:</strong> Goal is to [specific outcome you want]</div>
                      <div><strong>Expectations:</strong> [Length], [tone], [format] requirements</div>
                      <div><strong>Facts:</strong> Include [specific details, names, numbers]</div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-3 rounded border border-green-200">
                    <div className="text-green-800 font-semibold">Result:</div>
                    <div className="text-green-700 text-sm">AI produces exactly what you need, in your voice, with your facts, first try.</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Lyra's Strategic Briefing Results</h4>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-blue-600">95%</div>
                  <div className="text-sm text-gray-600">First drafts require zero editing</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-cyan-600">10x</div>
                  <div className="text-sm text-gray-600">Faster content creation</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">100%</div>
                  <div className="text-sm text-gray-600">Professional AI direction skills</div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button
                onClick={() => {
                  completePath('timewaste');
                  setCurrentStep('conclusion');
                }}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700"
              >
                Get Lyra's Complete Strategic Briefing System →
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'conclusion') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-cyan-500 mx-auto mb-4 flex items-center justify-center shadow-md">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              You've Found Your AI Solution!
            </h2>
            <p className="text-xl text-gray-600">
              Now you know exactly which system will solve your biggest AI challenge
            </p>
          </div>

          {/* Your Path Summary */}
          {selectedPainPoint && (
            <Card className="mb-8 border-2 border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-center text-green-800">Your Chosen Path</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 rounded-2xl bg-${characters[selectedPainPoint].color}-100 flex items-center justify-center`}>
                    <SupabaseIcon
                      iconPath={characters[selectedPainPoint].iconPath}
                      alt={characters[selectedPainPoint].name}
                      size="lg"
                      className="w-10 h-10"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{characters[selectedPainPoint].name}</h3>
                    <p className="text-green-700 font-semibold">{characters[selectedPainPoint].solution}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Steps */}
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-gray-900 text-center">Ready for Complete Mastery?</h3>
            <p className="text-gray-600 max-w-3xl mx-auto text-center">
              You've experienced one powerful system. The complete Lyra AI Mentor platform includes all three transformation paths plus 40+ additional lessons, 135+ professional prompts, and personal AI coaching.
            </p>

            {/* Path Options */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className={`${completedPaths.includes('overwhelms') ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-purple-100 mx-auto mb-3 flex items-center justify-center">
                    {completedPaths.includes('overwhelms') ?
                      <CheckCircle className="w-6 h-6 text-green-600" /> :
                      <Target className="w-6 h-6 text-purple-600" />
                    }
                  </div>
                  <h4 className="font-semibold text-gray-800">Quality Spotting</h4>
                  <p className="text-sm text-gray-600">Maya's System</p>
                  {!completedPaths.includes('overwhelms') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => handlePainPointSelect('overwhelms')}
                    >
                      Try This Path
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className={`${completedPaths.includes('generic') ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-pink-100 mx-auto mb-3 flex items-center justify-center">
                    {completedPaths.includes('generic') ?
                      <CheckCircle className="w-6 h-6 text-green-600" /> :
                      <Sparkles className="w-6 h-6 text-pink-600" />
                    }
                  </div>
                  <h4 className="font-semibold text-gray-800">Mission Alignment</h4>
                  <p className="text-sm text-gray-600">Sofia's System</p>
                  {!completedPaths.includes('generic') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => handlePainPointSelect('generic')}
                    >
                      Try This Path
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className={`${completedPaths.includes('timewaste') ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto mb-3 flex items-center justify-center">
                    {completedPaths.includes('timewaste') ?
                      <CheckCircle className="w-6 h-6 text-green-600" /> :
                      <Zap className="w-6 h-6 text-blue-600" />
                    }
                  </div>
                  <h4 className="font-semibold text-gray-800">Strategic Briefing</h4>
                  <p className="text-sm text-gray-600">Lyra's System</p>
                  {!completedPaths.includes('timewaste') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => handlePainPointSelect('timewaste')}
                    >
                      Try This Path
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* CTA Section */}
            <div className="text-center space-y-6">
              <div className="space-y-4">
                <Button
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white shadow-md text-lg px-8 py-3"
                  onClick={() => window.open('https://www.hellolyra.app', '_blank')}
                >
                  <Users className="w-5 h-5 mr-2" />
                  Start Your Complete AI Transformation
                </Button>

                <div className="text-sm text-gray-500 max-w-2xl mx-auto">
                  Join Maya, Sofia, Lyra and other professionals • 40+ hands-on lessons • 135+ proven prompts • Personal AI coaching
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6 max-w-2xl mx-auto">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 mb-2">Ready to transform your nonprofit's AI capabilities?</h4>
                  <p className="text-gray-600 text-sm">
                    You've seen the power of systematic AI mastery. The complete platform gives you all the tools, training, and support to become your organization's AI leader.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DemoOption2;